import React, { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import MatkaLandingpage from "./components/matkaComponents";
import { getColleciton, updateCollection } from "./services/firebase";
import ErrorPage from "./components/commonComponents/errorPage";
import {
  ADMIN_LOGIN_KEY,
  FIREBASE_COLLECTION_DOC_KEY,
  FIREBASE_COLLECTION_KEY,
  ROUTE_PATHS,
  SUPER_ADMIN_LOGIN_KEY,
} from "./constants";
import AdminPage from "./components/matkaComponents/admin";
import AddGameScreen from "./components/matkaComponents/AddGameScreen";
import AddFatafatResultScreen from "./components/matkaComponents/AddFatafatResultScreen";
import matkaPlayStaticData from "./data/matkaData.json";

import "./components/matkaComponents/css/matkaplay.css";
import PanelChartDetailScreen from "./components/matkaComponents/PanelChartDetailScreen";
import { NavBarHeader } from "./components/matkaComponents/components";
import {
  EMPTY_GUESS_WEEK,
  EMPTY_PATTI_WEEK,
  createGamesForCurrentDay,
  getCurrentDayGames,
  getWeekRecord,
  msUntilDailyReset,
  normalizeMatkaDocument,
  removeItemFromLocalStorage,
} from "./util";
import JodiChartDetailScreen from "./components/matkaComponents/JodiChartDetailScreen";
import HTMLContentScreen from "./components/matkaComponents/HTMLContentScreen";
import FatafatDetailScreen from "./components/matkaComponents/FatafatDetailScreen";
import EditFatafatResultScreen from "./components/matkaComponents/EditFatafatResultScreen";
import AddEditGuessingTableScreen from "./components/matkaComponents/AddEditGuessingTableScreen";
import ServerDownScreen from "./components/matkaComponents/ServerDownScreen";

// API client is intentionally not started. Login, reads, and writes go through Firebase.
// import ApiService from "./services/fetchAPI";
// import { API_CONFIGS, BASE_URL_K_BOMBAY } from "./services/fetchAPI/constants";
// export const APIService = new ApiService(BASE_URL_K_BOMBAY);

const pickMatkaDocument = (response) => {
  if (!Array.isArray(response) || response.length === 0) {
    return null;
  }
  return (
    response.find((item) => item?.id === FIREBASE_COLLECTION_DOC_KEY) ||
    response[0]
  );
};

function App() {
  const navigate = useNavigate();

  const [isSuperAdminLoggedIn, setSuperAdminLoggedIn] = useState(false);
  const [isAdminLoggedIn, setAdminLoggedIn] = useState(false);
  const [isLoading, setLoadingState] = useState<boolean>(true);
  const [landingPageData, setLandingPageData] = useState<any>({});
  const [gamesList, setGamesList] = useState<any>([]);
  const [gamesListHistory, setGamesListHistory] = useState<any>([]);
  const [guessingTableList, setGuessingTableList] = useState<any>([]);
  const [fatafatTableList, setFatafatTableList] = useState<any>([]);
  const [gamesTimeTableList, setGamesTimeTableList] = useState<any>([]);
  const [newGuessTableData, setNewGuessTableData] = useState<any>(EMPTY_GUESS_WEEK);
  const [pattiTableData, setPattiTableData] = useState<any>(EMPTY_PATTI_WEEK);
  const dailyTimerRef = useRef<number | null>(null);
  const creatingDailyGamesRef = useRef(false);
  const scheduleDailyGamesRef = useRef<(doc: any) => void>(() => {});

  const syncMatkaDocument = useCallback((rawDoc) => {
    const doc = normalizeMatkaDocument(rawDoc);
    setLandingPageData(doc);
    setGamesList(getCurrentDayGames(doc.gameTypes));
    setGamesListHistory(doc.gameTypes);
    setGuessingTableList(doc.guessingTableData);
    setFatafatTableList(doc.fatafatResults);
    setGamesTimeTableList(doc.gamesTimeTable);
    setNewGuessTableData(getWeekRecord(doc.guessDayRecords, EMPTY_GUESS_WEEK));
    setPattiTableData(getWeekRecord(doc.pattiRecords, EMPTY_PATTI_WEEK));
    return doc;
  }, []);

  const applyDocument = useCallback((rawDoc) => {
    const doc = syncMatkaDocument(rawDoc);
    checkLoginStatus(doc.adminAuthUsers);
    setLoadingState(false);
    return doc;
  }, [syncMatkaDocument]);

  const armNextDailyCheck = useCallback(() => {
    if (dailyTimerRef.current) {
      window.clearTimeout(dailyTimerRef.current);
      dailyTimerRef.current = null;
    }
    const wait = msUntilDailyReset();
    dailyTimerRef.current = window.setTimeout(() => {
      getColleciton(FIREBASE_COLLECTION_KEY)
        .then((response) => {
          const latest = pickMatkaDocument(response);
          if (latest) {
            scheduleDailyGamesRef.current(normalizeMatkaDocument(latest));
          }
        })
        .catch((error) => {
          console.error("Daily 9:00 IST game create failed to reload Firebase", error);
          armNextDailyCheck();
        });
    }, wait);
  }, []);

  const scheduleDailyGames = useCallback((doc) => {
    if (creatingDailyGamesRef.current) {
      return;
    }
    creatingDailyGamesRef.current = true;
    createGamesForCurrentDay(doc, (created) => {
      creatingDailyGamesRef.current = false;
      if (created) {
        window.location.reload();
        return;
      }
      armNextDailyCheck();
    });
  }, [armNextDailyCheck]);

  scheduleDailyGamesRef.current = scheduleDailyGames;

  useEffect(() => {
    fetchDataFromFirebase();
    return () => {
      if (dailyTimerRef.current) {
        window.clearTimeout(dailyTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchDataFromFirebase = () => {
    getColleciton(FIREBASE_COLLECTION_KEY)
      .then((response) => {
        const existing = pickMatkaDocument(response);
        if (existing) {
          const normalized = normalizeMatkaDocument(existing);
          const patch: any = {};
          if (!normalized.gamesTimeTable?.length && matkaPlayStaticData.gamesTimeTable?.length) {
            patch.gamesTimeTable = matkaPlayStaticData.gamesTimeTable;
          }
          if (!normalized.adminAuthUsers?.length && matkaPlayStaticData.adminAuthUsers?.length) {
            patch.adminAuthUsers = matkaPlayStaticData.adminAuthUsers;
          }
          if (
            !normalized.fatafat_result_timings?.length &&
            matkaPlayStaticData.fatafat_result_timings?.length
          ) {
            patch.fatafat_result_timings = matkaPlayStaticData.fatafat_result_timings;
          }
          if (Object.keys(patch).length > 0) {
            updateCollection(
              FIREBASE_COLLECTION_KEY,
              FIREBASE_COLLECTION_DOC_KEY,
              patch
            ).catch((error) => {
              console.error("firebase repair save failed", error);
            });
          }
          const doc = applyDocument({ ...normalized, ...patch });
          scheduleDailyGames(doc);
          return;
        }

        // createColleciton is not called. A missing document must not be written
        // over live results. The first app leaves this create commented out too.
        applyDocument(matkaPlayStaticData);
      })
      .catch((e) => {
        console.error("firebase getColleciton error", e);
        applyDocument(matkaPlayStaticData);
      });
  };
  const checkLoginStatus = useCallback((authData) => {
    const adminCredentials = localStorage.getItem(ADMIN_LOGIN_KEY);
    const parsedAdminCredentials =
      adminCredentials !== null ? JSON.parse(adminCredentials) : null;
    const superAdminCredentials = localStorage.getItem(SUPER_ADMIN_LOGIN_KEY);
    const parsedSuperAdminCredentials =
      superAdminCredentials !== null ? JSON.parse(superAdminCredentials) : null;

    if (
      parsedAdminCredentials &&
      Array.isArray(authData) &&
      authData[0] &&
      authData[0].userName === parsedAdminCredentials.userName &&
      authData[0].password === parsedAdminCredentials.password
    ) {
      setAdminLoggedIn(true);
    }
    // else if (authData.length > 1 && authData[1].userName === parsedSuperAdminCredentials.userName && authData[1].password === parsedSuperAdminCredentials.password) {
    //   setSuperAdminLoggedIn(true);
    // }
  }, []);

  const onAdminLoginSuccess = useCallback(
    (isSuperAdmin) => {
      navigate(ROUTE_PATHS.HOME);
      isSuperAdmin ? setSuperAdminLoggedIn(true) : setAdminLoggedIn(true);
    },
    [navigate]
  );

  const handleLogout = useCallback((e) => {
    e.preventDefault();
    removeItemFromLocalStorage(ADMIN_LOGIN_KEY);
    window.location.reload();
  }, []);
  // console.log("adfadfasd", landingPageData?.serverInfo?.isServerDown);
  return (
    <>
      {isLoading ? (
        <div className="container">
          <h1>Loading data...</h1>
        </div>
      ) : (
        <>
          <NavBarHeader
            isAdminLoggedIn={isAdminLoggedIn}
            handleLogout={handleLogout}
            isServerDown={Boolean(landingPageData?.serverInfo?.isServerDown)}
          />
          <div style={{ marginTop: 60 }}>
            <Routes>
              <Route
                path={ROUTE_PATHS.HOME}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.homePage &&
                        landingPageData?.metaData?.homePage.map(
                          (item, index) => {
                            if (item.tagName === "title") {
                              return (
                                <title key={`${item}-${index}`}>
                                  {item.tagValue}
                                </title>
                              );
                            }
                            return (
                              <meta
                                key={`${item}-${index}`}
                                name={item.tagName}
                                content={item.tagValue}
                              />
                            );
                          }
                        )}
                      <link
                        rel="canonical"
                        href="https://www.tatamatkaplay.com"
                      />
                    </Helmet>
                    {!landingPageData?.serverInfo?.isServerDown ? (
                      <MatkaLandingpage
                        matkaPlayData={landingPageData}
                        adminLoggedInSuccess={isAdminLoggedIn}
                        gamesList={gamesList}
                        gamesListHistory={gamesListHistory}
                        guessingTableList={guessingTableList}
                        fatafatTableList={fatafatTableList}
                        gamesTimeTableList={gamesTimeTableList}
                        newGuessTableData={newGuessTableData}
                        pattiTableData={pattiTableData}
                      />
                    ) : (
                      <ServerDownScreen />
                    )}
                  </>
                }
              />
              <Route
                path={ROUTE_PATHS.ADMIN_LOGIN}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <AdminPage
                      data={landingPageData["adminAuthUsers"]}
                      onAdminLoginSuccess={onAdminLoginSuccess}
                    />
                  </>
                }
              />

              <Route
                path={`${ROUTE_PATHS.ADD_GAME}/:gameIndex?`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <AddGameScreen
                      data={landingPageData}
                      gamesList={gamesList}
                      guessingTableList={guessingTableList}
                      fatafatTableList={fatafatTableList}
                      gamesTimeTableList={gamesTimeTableList}
                      onGamesSaved={syncMatkaDocument}
                    />
                  </>
                }
              />

              <Route
                path={`${ROUTE_PATHS.ADD_FATAFAT_RESULT}/:fatafatGuessId?`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <AddFatafatResultScreen
                      data={landingPageData}
                      fatafatTableList={fatafatTableList}
                    />
                  </>
                }
              />
              <Route
                path={`${ROUTE_PATHS.FATAFAT_RESULT_DETAIL}`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <FatafatDetailScreen
                      data={landingPageData}
                      fatafatTableList={fatafatTableList}
                    />
                  </>
                }
              />
              <Route
                path={`${ROUTE_PATHS.EDIT_FATAFAT_RESULT_DETAIL}/:fatafatGuessId?`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <EditFatafatResultScreen
                      data={landingPageData}
                      fatafatTableList={fatafatTableList}
                    />
                  </>
                }
              />

              <Route
                path={`${ROUTE_PATHS.ADD_EDIT_GUESSING_TABLE_RESULT}/:guessGameId?`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <AddEditGuessingTableScreen
                      data={landingPageData}
                      guessingTableList={guessingTableList}
                    />
                  </>
                }
              />

              <Route
                path={`${ROUTE_PATHS.PANEL_CHARTS_DETAIL}/:gameName`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <PanelChartDetailScreen
                      data={gamesListHistory ?? landingPageData.gameTypes}
                    />
                  </>
                }
              />
              <Route
                path={`${ROUTE_PATHS.JDOI_CHARTS_DETAIL}/:gameName`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <JodiChartDetailScreen
                      data={gamesListHistory ?? landingPageData.gameTypes}
                    />
                  </>
                }
              />

              <Route
                path={`${ROUTE_PATHS.HTML_CONTENT_SCREEN}/:hrefVal`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <HTMLContentScreen
                      data={gamesListHistory ?? landingPageData.gameTypes}
                    />
                  </>
                }
              />

              <Route
                path={`${ROUTE_PATHS.SERVER_DOWN_SCREEN}`}
                errorElement={<ErrorPage />}
                element={
                  <>
                    <Helmet>
                      {landingPageData?.metaData?.contactusPage &&
                        landingPageData?.metaData?.contactusPage.map((item) => {
                          if (item.tagName === "title") {
                            return <title>{item.tagValue}</title>;
                          }
                          return (
                            <meta name={item.tagName} content={item.tagValue} />
                          );
                        })}
                      <link
                        rel="canonical"
                        href="https://www.kbombaysattamatka.com/contact-us"
                      />
                    </Helmet>
                    <ServerDownScreen />
                  </>
                }
              />
            </Routes>
            {/* <Footer data={landingPageData["contact-us"]} /> */}
          </div>
        </>
      )}
    </>
  );
}

export default App;
